import PausedIcon from "@assets/icons/pause.svg?react";
import defaultThumbnail from "@src/app/assets/default-thumbnail.jpg";
import TimeCode from "@src/app/components/TimeCode/TimeCode";
import cx from "classnames";
import type { MouseEvent, ReactElement } from "react";

import VideoPlayingIcon from "../VideoPlayingIcon/VideoPlayingIcon";

type Props = Readonly<{
  title: string;
  thumbnailSrc?: string;
  youtubeId: string;
  youtubeSrc: string;
  timeCode: string;
  isPlaying?: boolean;
  isPaused?: boolean;
  isEnded?: boolean;
  isSelected?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}>;

export default function VideoWidget({
  title,
  thumbnailSrc,
  youtubeId,
  youtubeSrc,
  timeCode,
  isPlaying = false,
  isPaused,
  isEnded,
  isSelected = false,
  onClick,
  ...props
}: Props): ReactElement {
  const youtubeThumbnail = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  return (
    <div
      id="video-widget"
      data-testid="video-widget"
      className={cx(
        "group hover:bg-background-secondary-on-dark grid cursor-pointer grid-cols-7 items-start gap-3 px-3 py-4 transition-all ease-in-out",
        {
          "bg-background-secondary-on-dark": isSelected,
          "bg-background-primary-on-dark": !isSelected,
        },
      )}
      data-url={youtubeSrc}
      onClick={onClick}
      role="presentation"
      {...props}
    >
      <div className="relative col-span-3">
        <img
          src={
            thumbnailSrc || (youtubeId ? youtubeThumbnail : defaultThumbnail)
          }
          alt={title}
          className="size-full object-cover"
          style={{ aspectRatio: "3 / 2" }}
        />
        {isSelected && isPlaying && (
          <div className="absolute top-0 flex size-full items-center justify-center text-white">
            <VideoPlayingIcon
              className="bg-background-overlay size-10 rounded-full"
              data-testid="video-playing-icon"
            />
          </div>
        )}

        {isSelected && isPaused && (
          <div className="absolute top-0 flex size-full items-center justify-center">
            <PausedIcon className="bg-background-overlay size-10 rounded-full p-2 text-white" />
          </div>
        )}

        {(!isSelected || isEnded) && (
          <TimeCode
            className="absolute right-0 bottom-0 m-2"
            time={timeCode}
            data-testid="time-code"
          />
        )}
      </div>

      <p
        className={cx(
          "text-label-2 col-span-4 line-clamp-4 max-h-[72px] font-bold group-hover:underline",
          isSelected ? "text-primary-on-dark" : "text-secondary-on-dark",
        )}
      >
        {title}
      </p>
    </div>
  );
}
